// eslint-disable-next-line import/named
import { AxiosResponse } from 'axios'
import React, { ChangeEvent, FC, useEffect, useState } from 'react'
import Modal from 'react-modal'
import NumberFormat from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import httpClient from '../../api/httpClient'
import info from '../../assets/images/info.svg'
import success from '../../assets/images/success.svg'
import { HeaderBar } from '../../component'
import {
  CashInfo,
  CashPayInInfo,
  ChequeInfo,
  ChequePayInInfo,
  CompanyChequeInfo,
  CreditCardInfo,
  QRCodeInfo
} from '../../component/detailPageComponent'
import { CancelModal } from '../../component/modaldialog/ModalCancel'
import { ConfirmModal } from '../../component/modaldialog/ModalConfirmPaid'
import ModalEditCustomerInfo from '../../component/modaldialog/ModalEditCustomerInfo'
import ModalTransferComplete from '../../component/modaldialog/ModalTransferComplete'
import { ACCOUNT_LOCK_MESSAGE, ETR_STATUS, server } from '../../constants'
import { RoundNumber } from '../../helpers/Number'
import { validateFileSize } from '../../helpers/file'
import { isBranchStaff, isCancelable, isCollectionStaff } from '../../helpers/roles'
import {
  DisplayDateFormat,
  DisplayStatusName,
  DisplayToThaiDateFormat,
  isMaskingData,
  maskingEmail,
  maskingMobileNo
} from '../../helpers/texthelper'
import { AgentModel } from '../../models/agent'
import { CancelModalModel, ConfirmModalModel } from '../../models/component'
import { DocumentModel } from '../../models/document'
import { DocumentDetailModel } from '../../models/documentDetail'
import { PaymentObjectiveModel } from '../../models/paymentObjective'
import {
  deleteChangeEmailOrMobileFile,
  getDocumentUploadFileBlob
} from '../../redux/actions/document.action'
import * as documentAction from '../../redux/actions/document.detail.action'
import * as editActions from '../../redux/actions/transaction.edit.action'
import { RootReducers } from '../../redux/reducers'
import './style.css'
import { setError } from '../../redux/actions/error.action'
import ShowButtonTransfer from '../../component/ShowButtonTransfer/ShowButtonTransfer'
import RequireBranchStaff from '../../component/RequireBranchStaff/RequireBranchStaff'

interface IChangeEmailOrMobile {
  onSuccess: () => void
}

const ChangeEmailOrMobile: FC<IChangeEmailOrMobile> = ({ onSuccess }) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const dispatch = useDispatch()
  const documentData = useSelector((state: RootReducers) => state.documentDetailReducer)
  const isBranchStaff = useSelector(
    (state: RootReducers) => state.loginReducer.account?.agentInfo.isBranchStaff
  )
  const isHideDeleteFile = [ETR_STATUS.CANCEL_ETR, ETR_STATUS.SUCCESS_ETR].includes(
    documentData.documentModel?.status as string
  )

  const viewUploadFile = async () => {
    if (!documentData?.documentModel?.changeEmailOrMobileFile) {
      return
    }

    setIsProcessing(true)

    try {
      const res = (await dispatch(
        getDocumentUploadFileBlob(documentData.documentModel.changeEmailOrMobileFile)
      )) as unknown as AxiosResponse
      window.open(window.URL.createObjectURL(res.data), '_blank')
    } catch (error) {
      dispatch(setError(error))
    } finally {
      setIsProcessing(false)
    }
  }

  const handleUploadChangeEmailOrMobileFile = async (e: ChangeEvent<HTMLInputElement>) => {
    try {
      setIsProcessing(true)
      const file = e.target.files?.[0]

      if (file) {
        if (!['image/jpeg', 'application/pdf'].includes(file.type)) {
          e.target.value = ''
          return
        }

        if (!validateFileSize(file.size)) {
          e.target.value = ''
          return
        }

        const formData = new FormData()
        formData.append('changeEmailOrMobile', file)
        formData.append('receiptNo', documentData.documentModel?.receiptNo ?? '')

        await httpClient.post(server.TRANSACTION_URL + '/uploadChangeEmailOrMobileFile', formData)
        await onSuccess()
      }
    } catch (error) {
      dispatch(setError(error))
    } finally {
      setIsProcessing(false)
    }
  }

  const deleteUploadChangeEmailOrMobileFile = async () => {
    if (!documentData?.documentModel?.receiptNo) {
      return
    }

    setIsProcessing(true)
    try {
      await dispatch(deleteChangeEmailOrMobileFile(documentData.documentModel.receiptNo))
      await onSuccess()
    } catch (error) {
      dispatch(setError(error))
    } finally {
      setIsProcessing(false)
    }
  }

  if (
    documentData.documentModel?.hasChangeEmailOrMobile &&
    documentData.documentModel?.changeEmailOrMobileFile
  ) {
    return (
      <>
        <button onClick={viewUploadFile} disabled={isProcessing} type="button">
          ดูเอกสารการเปลี่ยนแปลงอีเมลหรือเบอร์โทร
        </button>
        {isBranchStaff && !isHideDeleteFile && (
          <>
            {' '}
            หรือ{' '}
            <button
              onClick={deleteUploadChangeEmailOrMobileFile}
              disabled={isProcessing}
              type="button"
            >
              ลบเอกสาร
            </button>
          </>
        )}
      </>
    )
  }

  if (
    documentData.documentModel?.hasChangeEmailOrMobile &&
    !documentData.documentModel?.changeEmailOrMobileFile &&
    isBranchStaff
  ) {
    return (
      <>
        โปรดอัพโหลดสาเหตุการเปลี่ยนแปลงอีเมลหรือเบอร์โทร{' '}
        <input
          type="file"
          accept="image/jpeg,application/pdf"
          onChange={handleUploadChangeEmailOrMobileFile}
          disabled={isProcessing}
        />
      </>
    )
  }

  return null
}

export const DetailPage: React.VFC = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const dispatch = useDispatch()
  const loginState = useSelector((state: RootReducers) => state.loginReducer)
  const documentData = useSelector((state: RootReducers) => state.documentDetailReducer)
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false)
  const [isOpenSuccessModal, setIsOpenSuccessModal] = useState<boolean>(false)
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState<boolean>(false)
  const [isOpenCancelModal, setIsOpenCancelModal] = useState<boolean>(false)
  const [isOpenTransferCompleteModal, setIsOpenTransferCompleteModal] = useState<boolean>(false)
  const [isDisableSubmitToDcr, setIsDisableSubmitToDcr] = useState(false)
  const [confirmData, setConfirmData] = useState<ConfirmModalModel>({
    eTRNo: '',
    id: 0,
    reasonText: ''
  })
  const [cancelData, setCancelData] = useState<CancelModalModel>({
    eTRNo: '',
    id: 0,
    reasonText: ''
  })
  const [isOpenEditCustomerModal, setIsOpenEditCustomerModal] = useState(false)

  const isShowChangeStatusEtrBillpayBtn =
    isBranchStaff(loginState.account?.agentInfo.role as string) ||
    isCollectionStaff(loginState.account?.agentInfo.role as string)

  const getData = async () => {
    try {
      if (id) {
        await dispatch(documentAction.handleGetDocumentById(id))
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const total = (): number => {
    let totalPaymentDetail = 0
    documentData.documentModel?.documentDetails?.forEach(item => {
      totalPaymentDetail += +item.paymentAmount
    })

    return RoundNumber(totalPaymentDetail)
  }

  const handleModalClose = () => {
    setIsOpenModal(!isOpenModal)
  }
  const handleModalSuccessClose = () => {
    setIsOpenSuccessModal(!isOpenSuccessModal)
  }

  const handleModalCancel = (status: string) => {
    if (status === ETR_STATUS.CANCEL_ETR) setIsOpenModal(!isOpenModal)
    if (
      (status === ETR_STATUS.SUCCESS_ETR || status === ETR_STATUS.SUBMITED_ETR) &&
      loginState.account?.agentInfo.isBranchStaff
    )
      setIsOpenSuccessModal(!isOpenSuccessModal)
  }

  const displayCreditCard = (creditCardNumber: string, creditCardNumberRaw: string) => {
    if (loginState.account?.agentInfo.isAO || loginState.account?.agentInfo.isBranchStaff)
      return creditCardNumberRaw

    return creditCardNumber
  }
  const displayCreditCardExpireDateFormat = () => {
    if (loginState.account?.agentInfo.isAO || loginState.account?.agentInfo.isBranchStaff)
      return '##/##'

    return '#X/YY'
  }

  const getStatusDate = (doc: DocumentModel | null) => {
    if (doc) {
      switch (doc.status) {
        case ETR_STATUS.CANCEL_ETR:
          return DisplayToThaiDateFormat(doc.canceldate as Date)

        case ETR_STATUS.SUCCESS_ETR:
          return DisplayToThaiDateFormat(doc.statusDate as Date)

        default:
          return DisplayToThaiDateFormat(doc.created as Date)
      }
    }

    return ''
  }

  const openEditCustomerModal = () => {
    setIsOpenEditCustomerModal(true)
  }

  const closeEditCustomerModal = () => {
    setIsOpenEditCustomerModal(false)
  }

  // ===== complete dialog begin---------
  const handleOpenTransferCompleteModal = (id: number, eTRNo: string) => {
    setIsOpenTransferCompleteModal(!isOpenTransferCompleteModal)
    console.log(`setIsOpenTransferCompleteModal id = ${id} | eTRNo = ${eTRNo}`)
    setConfirmData({ id: id, eTRNo: eTRNo, reasonText: '' })
  }
  const handleCloseTransferCompleteModal = () => {
    setIsOpenTransferCompleteModal(false)
  }

  // ===== complete dialog end ---------

  // ===== confirm dialog begin---------
  const handleOpenModalConfirmCancelButton = (id: number, eTRNo: string) => {
    setIsOpenConfirmModal(!isOpenConfirmModal)
    console.log(`handleOpenModalConfirmCancelButton id = ${id} | eTRNo = ${eTRNo}`)
    setConfirmData({ id: id, eTRNo: eTRNo, reasonText: '' })
  }
  const handleModalConfirmCancelButton = (disableSubmitToDcr = false) => {
    setIsOpenConfirmModal(false)
    setIsDisableSubmitToDcr(disableSubmitToDcr)
  }

  // ===== confirm dialog end ---------

  // ===== Cencel dialog  begin ---------

  const handleOnCencelOpen = (id: number, eTRNo: string) => {
    setIsOpenCancelModal(!isOpenCancelModal)
    console.log(`handleOpenModalConfirmCancelButton id = ${id} | eTRNo = ${eTRNo}`)
    setCancelData({ id: id, eTRNo: eTRNo, reasonText: '' })
  }

  const handleCancelOkButton = (data: CancelModalModel) => {
    const date = new Date()
    if (data.reasonText.length > 0) {
      const statusDate = new Date(
        Date.UTC(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          date.getHours(),
          date.getMinutes()
        )
      )
      const docData: DocumentModel = {
        id: data.id,
        status: ETR_STATUS.CANCEL_ETR,
        remark: data.reasonText,
        updateBy: loginState.account?.agentInfo.userName
          ? loginState.account?.agentInfo.userName
          : loginState.account?.agentInfo.agentId,
        canceldate: statusDate,
        etrStatusDate: statusDate
      }
      dispatch(editActions.cancelTransaction(data.id, docData, navigate))
      setIsOpenModal(false)
      navigate('/summary', {
        state: {
          message: `ยกเลิกใบรับเงินชั่วคราว<br/>
        <b>หมายเลข ${data.eTRNo}</b><br/>
        เรียบร้อยแล้ว`,
          icon: success,
          gobackUrl: `/documentDetail/${data.id}`
        }
      })
    }
  }

  const handleCanceldialog = () => {
    setIsOpenCancelModal(!isOpenCancelModal)
  }

  const RequireNewBusiness = ({ children }: any) => {
    return [1].includes(documentData.documentModel?.paymentObjectives?.[0].paymentObjectiveType?.id)
      ? children
      : null
  }
  const ShowNBPolicy = ({ children }: any) => {
    return documentData.documentModel?.policyNo ? null : children
  }

  // ===== Cencel dialog end ---------
  const renderPaymentObjective = (paymentObjectives: PaymentObjectiveModel[]) => {
    paymentObjectives &&
      paymentObjectives.sort((a: PaymentObjectiveModel, b: PaymentObjectiveModel) => {
        return a.paymentObjectiveType.id - b.paymentObjectiveType.id
      })
    return (
      paymentObjectives &&
      paymentObjectives.map((item: PaymentObjectiveModel) => {
        return (
          <div className="flex-item" key={item.id}>
            <div className="flex-container-detail">
              <div className="flex-item-detail">
                <li>{item.paymentObjectiveType.name}</li>
              </div>
              <div className="flex-item-detail amount-right">
                <span>
                  <NumberFormat
                    thousandsGroupStyle="thousand"
                    value={item.amount}
                    prefix=""
                    decimalSeparator="."
                    displayType="text"
                    type="text"
                    thousandSeparator={true}
                    allowNegative={true}
                    decimalScale={2}
                    fixedDecimalScale={true}
                  />
                </span>{' '}
                บาท
              </div>
            </div>
          </div>
        )
      })
    )
  }

  const rendderDocumentDetail = (documentDetails: DocumentDetailModel[]) => {
    documentDetails &&
      documentDetails.sort((a: DocumentDetailModel, b: DocumentDetailModel) => {
        return a.paymentMethod.orderBy - b.paymentMethod.orderBy
      })
    return (
      documentDetails &&
      documentDetails.map((item: DocumentDetailModel) => {
        item.documentID = documentData.documentModel?.id
        switch (item.paymentMethod.id) {
          case 1:
            return (
              <div>
                <CashInfo item={item}></CashInfo>
              </div>
            )
          case 2:
            return <ChequeInfo item={item}></ChequeInfo>

          case 3:
            return <CreditCardInfo item={item}></CreditCardInfo>
          case 4:
            return (
              <>
                <div className="flex-container">
                  <div className="flex-item-left">
                    <div className="flex-container-cc">
                      <div className="flex-item-cc-left">
                        <li>
                          <span>{item.paymentMethod.name}</span>{' '}
                        </li>
                      </div>
                      <div className="flex-item-cc-right">หมายเหตุ :{item.other_Pay}</div>
                    </div>
                  </div>
                  <div className="flex-item-right amount-right">
                    <span>
                      <NumberFormat
                        thousandsGroupStyle="thousand"
                        value={item.paymentAmount}
                        prefix=""
                        decimalSeparator="."
                        displayType="text"
                        type="text"
                        thousandSeparator={true}
                        allowNegative={true}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </span>{' '}
                    บาท
                  </div>
                </div>
              </>
            )
          case 5:
            return <CashPayInInfo item={item}></CashPayInInfo>
          case 6:
            return <ChequePayInInfo item={item}></ChequePayInInfo>
          case 7:
            return <CompanyChequeInfo item={item}></CompanyChequeInfo>
          case 8:
            return <QRCodeInfo item={item}></QRCodeInfo>
          default:
            break
        }
      })
    )
  }

  const isDisableConfirmPaid = [ETR_STATUS.SUCCESS_ETR, ETR_STATUS.CANCEL_ETR].includes(
    documentData.documentModel?.status as string
  )

  return (
    <div>
      <HeaderBar />
      <div className="container">
        <div className="content-detail">
          <div className="cotent-header">
            <div className="first-Header">
              เลขที่ใบรับเงินชั่วคราว : <span>{documentData.documentModel?.receiptNo}</span>
            </div>
            <div className="first-Header">
              สถานะ : {DisplayStatusName(documentData.documentModel?.status)}
              <img
                className="image-info"
                src={info}
                alt="info"
                onClick={() => handleModalCancel(documentData.documentModel?.status as string)}
              ></img>
            </div>
          </div>
          <div className="cotent-header">
            <div className="second-Header">
              <div>
                วันที่ออกใบรับเงินชั่วคราว :
                <span> {DisplayToThaiDateFormat(documentData.documentModel?.receiptdate)}</span>
              </div>
              <div>
                เหตุผลที่ส่งงานล่าช้า : {documentData.documentModel?.submissionDelayReason || '-'}
              </div>
            </div>
            <div>
              <div className="status_date">
                <span>
                  {' '}
                  วันที่ปรับสถานะ : <strong>{getStatusDate(documentData.documentModel)}</strong>
                </span>
              </div>
            </div>
          </div>
          <div className="agent-Detail">
            <div className="bold">ข้อมูลตัวแทน</div>
            <hr></hr>
            <div className="flex-container">
              <div className="flex-item">ตัวแทน : {documentData.documentModel?.agent?.name}</div>
              <div className="flex-item">
                รหัสตัวแทน : {documentData.documentModel?.agent?.agentId}
              </div>
              <div className="flex-item">อีเมล : {documentData.documentModel?.agent?.email}</div>
              <div className="flex-item">
                สาขา : {documentData.documentModel?.agent?.branchCode} -{' '}
                {documentData.documentModel?.branchName ||
                  documentData.documentModel?.agent?.branchName}{' '}
              </div>
            </div>
          </div>
          <div className="customer-Detail">
            <div className="bold">ข้อมูลกรมธรรม์ลูกค้า</div>
            <hr></hr>
            <div className="flex-container">
              <div className="flex-item">
                เลขที่กรมธรรม์ : {documentData.documentModel?.policyNo}
              </div>
              <div className="flex-item">
                ผู้เอาประกัน : {documentData.documentModel?.insuredName}
              </div>
              <div className="flex-item">แบบประกัน : {documentData.documentModel?.planName}</div>
              <div className="flex-item">
                วันเดือนปีเกิด(ค.ศ.) : {DisplayDateFormat(documentData.documentModel?.customerDOB)}
              </div>
              <div className="flex-item">
                จำนวนเงินเอาประกัน (บาท) :{' '}
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={documentData.documentModel?.sumAssured}
                  prefix=""
                  decimalSeparator="."
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </div>
              <div className="flex-item">
                อีเมล :{' '}
                {isMaskingData(
                  loginState.account?.agentInfo as AgentModel,
                  documentData.documentModel?.serviceAgent as string,
                  loginState.account?.agentInfo.AgentUnderAOs
                )
                  ? maskingEmail(documentData.documentModel?.customerMail as string)
                  : documentData.documentModel?.customerMail}
              </div>
              <div className="flex-item">
                งวดชำระ : {documentData.documentModel?.paymentFrequency}
              </div>
              <div className="flex-item">
                เบอร์โทรศัพท์ :{' '}
                {isMaskingData(
                  loginState.account?.agentInfo as AgentModel,
                  documentData.documentModel?.serviceAgent as string,
                  loginState.account?.agentInfo.AgentUnderAOs
                )
                  ? maskingMobileNo(documentData.documentModel?.customerPhone as string)
                  : documentData.documentModel?.customerPhone}
                {/* <NumberFormat
                  value={documentData.documentModel?.customerPhone}
                  prefix=""
                  format="### ### ####"
                  displayType="text"
                  type="text"
                /> */}
              </div>
              <div className="flex-item">
                {/* <RequireNewBusiness>
                  <ShowNBPolicy> */}
                เลขที่กรมธรรม์รายใหม่ : {documentData.documentModel?.NB_PolicyNo}
                {/* </ShowNBPolicy>
                </RequireNewBusiness> */}
              </div>
            </div>
            <div className="customer-Detail-action">
              <RequireBranchStaff>
                <RequireNewBusiness>
                  <button
                    className="btn-edit"
                    onClick={openEditCustomerModal}
                    disabled={
                      documentData.documentModel?.status === ETR_STATUS.SUCCESS_ETR ||
                      documentData.documentModel?.status === ETR_STATUS.CANCEL_ETR ||
                      documentData.documentModel?.status === ETR_STATUS.SUBMITED_ETR
                    }
                  >
                    ข้อมูลเพิ่มเติม
                  </button>
                </RequireNewBusiness>
              </RequireBranchStaff>
            </div>
          </div>
          <div className="payment-Detail">
            <div className="bold">รายการการรับเงินค่าเบี้ยประกันภัย และอื่นๆ</div>
            <hr></hr>
            <div className="payer">ได้รับเงินจาก : {documentData.documentModel?.payerName}</div>
            <div className="pay-for">เพื่อชำระ :</div>
            <div className="flex-container">
              {renderPaymentObjective(
                documentData.documentModel?.paymentObjectives as PaymentObjectiveModel[]
              )}
            </div>
            <div className="pay-with">ชำระโดย :</div>
            <div className="doc-detail-container">
              {rendderDocumentDetail(
                documentData.documentModel?.documentDetails as DocumentDetailModel[]
              )}
            </div>
            <ChangeEmailOrMobile onSuccess={getData} />
            <div className="total-container">
              <div className="flex-container">
                <div className="flex-item-left">จำนวนเบี้ยชำระทั้งหมด :</div>
                <div className="flex-item-right amount-right">
                  <span>
                    <NumberFormat
                      thousandsGroupStyle="thousand"
                      value={total()}
                      prefix=""
                      decimalSeparator="."
                      displayType="text"
                      type="text"
                      thousandSeparator={true}
                      allowNegative={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </span>{' '}
                  บาท
                </div>
              </div>
            </div>
            <div className="button-container">
              <button
                className="btn-change-status"
                style={{
                  display: isShowChangeStatusEtrBillpayBtn ? 'inline-block' : 'none',
                  width: 'max-content',
                  paddingInline: 20
                }}
                disabled={isDisableConfirmPaid}
                onClick={() =>
                  handleOpenTransferCompleteModal(
                    documentData.documentModel?.id as number,
                    documentData.documentModel?.receiptNo as string
                  )
                }
              >
                ปรับสถานะ e-TR Billpay/กธ.IL
              </button>
              <button
                className="cancel-button"
                disabled={
                  documentData.documentModel?.status === ETR_STATUS.CANCEL_ETR ||
                  documentData.documentModel?.status === ETR_STATUS.SUBMITED_ETR ||
                  documentData.documentModel?.status === ETR_STATUS.SUCCESS_ETR ||
                  !isCancelable(
                    loginState.account?.agentInfo.role as string,
                    documentData.documentModel?.createBy as string,
                    loginState.account?.agentInfo.agentId as string
                  ) ||
                  loginState.account?.agentInfo.accountLock
                }
                onClick={() =>
                  handleOnCencelOpen(
                    documentData.documentModel?.id as number,
                    documentData.documentModel?.receiptNo as string
                  )
                }
              >
                <div className="tooltip">
                  <span
                    className={`${
                      loginState.account?.agentInfo.accountLock
                        ? 'account_lock_tooltiptext '
                        : 'hide'
                    }`}
                  >
                    {ACCOUNT_LOCK_MESSAGE}
                  </span>
                  ยกเลิก
                </div>
              </button>

              <ShowButtonTransfer>
                <button
                  className="confirm-paid-button"
                  style={{
                    width: 'max-content',
                    paddingInline: 20
                  }}
                  disabled={
                    documentData.documentModel?.status === ETR_STATUS.SUCCESS_ETR ||
                    documentData.documentModel?.status === ETR_STATUS.CANCEL_ETR ||
                    documentData.documentModel?.status === ETR_STATUS.SUBMITED_ETR ||
                    isDisableSubmitToDcr
                  }
                  onClick={() =>
                    handleOpenModalConfirmCancelButton(
                      documentData.documentModel?.id as number,
                      documentData.documentModel?.receiptNo as string
                    )
                  }
                >
                  นำส่งเข้าบริษัท
                </button>
              </ShowButtonTransfer>
            </div>
          </div>
        </div>
      </div>
      <div className="footer"></div>
      <Modal
        isOpen={isOpenModal}
        className="modal-cancel"
        style={{
          overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(24, 48, 40, 0.9)'
          }
        }}
        ariaHideApp={false}
      >
        <div className="modal-cancel-content">
          <div>
            <div className="modal-cancel-header-text">เหตุผลในการยกเลิกใบรับเงินชั่วคราว</div>
            <div className="modal-cancel-detail">
              {documentData?.documentModel?.remark}
              <br />
              <div
                className={`cancel-by ${
                  loginState.account?.agentInfo.isBranchStaff ? '' : ' invisibility'
                }`}
              >
                <hr />
                ทำรายการโดย: {documentData?.documentModel?.updateBy}
                <br />
                วันที่ทำรายการ:{' '}
                {DisplayToThaiDateFormat(documentData?.documentModel?.canceldate as Date)}
              </div>
            </div>
          </div>
          <div className="modal-cancel-button">
            <button
              className="confirm-transaction-button button-close"
              onClick={() => handleModalClose()}
            >
              ปิด
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isOpenSuccessModal}
        className="modal-cancel"
        style={{
          overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(24, 48, 40, 0.9)'
          }
        }}
        ariaHideApp={false}
      >
        <div className="modal-cancel-content">
          <div>
            <div className="modal-cancel-header-text">เหตุผลนำส่งใบรับเงินชั่วคราว</div>
            <div className="modal-cancel-detail">
              {documentData?.documentModel?.statusReason}
              <br />
              <hr />
              ทำรายการโดย: {documentData?.documentModel?.updateBy}
              <br />
              วันที่ทำรายการ:{' '}
              {DisplayToThaiDateFormat(documentData?.documentModel?.statusDate as Date)}
            </div>
          </div>
          <div className="modal-cancel-button">
            <button
              className="confirm-transaction-button button-close"
              onClick={() => handleModalSuccessClose()}
            >
              ปิด
            </button>
          </div>
        </div>
      </Modal>

      <CancelModal
        handleOkButton={handleCancelOkButton}
        handleCancelbutton={handleCanceldialog}
        isOpenModal={isOpenCancelModal}
        data={cancelData}
      ></CancelModal>

      <ConfirmModal
        handleCancelbutton={handleModalConfirmCancelButton}
        isOpenModal={isOpenConfirmModal}
        data={confirmData}
      ></ConfirmModal>

      <ModalTransferComplete
        isOpenModal={isOpenTransferCompleteModal}
        data={confirmData}
        handleCancelbutton={handleCloseTransferCompleteModal}
      />

      <ModalEditCustomerInfo isOpen={isOpenEditCustomerModal} onClose={closeEditCustomerModal} />
    </div>
  )
}
